import React from 'react'
import { useTranslation } from 'react-i18next'
import { BaseEdge, EdgeLabelRenderer, getStraightPath, type EdgeProps } from '@xyflow/react'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'

import { EDGE_PATH_STYLE } from './layout'

export type InsertStepEdgeData = {
  /** Position the new step takes in `steps` — edge i sits before step i, and
   *  the last edge (Start/step → Publish) appends. */
  insertIndex: number
  onInsert: (index: number) => void
}

/**
 * Connector between two workflow nodes carrying a "+" button at its midpoint.
 * The button lives in an `EdgeLabelRenderer` portal (outside the SVG layer) so
 * it can be a real shadcn Button rather than an SVG shape; its only positional
 * style is the transform React Flow's API hands us, which cannot be a Tailwind
 * class because the coordinates are computed per render.
 */
const InsertStepEdge: React.FC<EdgeProps> = ({ id, sourceX, sourceY, targetX, targetY, data }) => {
  const { t } = useTranslation()
  const [edgePath, labelX, labelY] = getStraightPath({ sourceX, sourceY, targetX, targetY })
  const insert = data as unknown as InsertStepEdgeData
  const labelPosition = { transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }

  return (
    <>
      <BaseEdge id={id} path={edgePath} style={EDGE_PATH_STYLE} />
      <EdgeLabelRenderer>
        <div className="nodrag nopan pointer-events-auto absolute" style={labelPosition}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 w-6 rounded-full bg-background p-0 shadow-sm"
            title={t('Submittals_wf_flow_insert_step')}
            onClick={() => insert.onInsert(insert.insertIndex)}
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </EdgeLabelRenderer>
    </>
  )
}

export const EDITOR_EDGE_TYPES = {
  insert: InsertStepEdge,
}

export default InsertStepEdge
